import React, { useState } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ThemeProvider, createTheme, CssBaseline, Box, AppBar, Toolbar, IconButton, Typography, CircularProgress } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';

import { AuthProvider, useAuthContext } from './context/AuthContext';
import { CompanyProvider } from './context/CompanyContext';
import Sidebar from './components/Sidebar';
import { useCompanyChange } from './hooks/useCompanyChange';
import Login from './screens/Login/Login';
import Register from './screens/Register/Register';
import Applications from './screens/Applications/Applications';
import Invoicing from './screens/Invoicing/Invoicing';
import Payroll from './screens/Payroll/Payroll';
import CompanyInformation from './screens/CompanyInformation/CompanyInformation';

// Custom Theme Configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#111', // Dark background
      contrastText: '#ffffff', // White text on dark buttons
    },
    secondary: {
      main: '#222222', // Dark charcoal
    },
    background: {
      default: '#f8f9fa', // Very light gray for page background
      paper: '#ffffff',
    },
    text: {
      primary: '#222222',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 600 },
    h3: { fontWeight: 600 },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 500 },
    h6: { fontWeight: 500 },
    button: {
      fontWeight: 600,
      textTransform: 'none', // Modern buttons usually don't use uppercase
    },
  },
  shape: {
    borderRadius: 12, // Softer, modern corners
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          padding: '10px 24px',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(134, 235, 133, 0.4)', // Subtle glow on hover
          },
        },
        containedPrimary: {
          backgroundColor: '#111',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#86eb85', // Green on hover
            color: '#111', // Dark text on green hover
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', // Cleaner shadow
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
          }
        }
      }
    }
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
});

// Protected Route Wrapper with Layout
const ProtectedRoute = () => {
  const { currentUser } = useAuthContext();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Handle data reloading when company changes
  const { isLoading: isCompanyChanging } = useCompanyChange();

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onCollapseChange={setSidebarCollapsed}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          minHeight: '100vh',
        }}
      >
        {/* Mobile Header */}
        <AppBar
          position="sticky"
          sx={{
            display: { xs: 'block', md: 'none' },
            backgroundColor: '#1a1a1a',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>
              CycleSaver Employer Portal
            </Typography>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, position: 'relative' }}>
          {isCompanyChanging && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                backdropFilter: 'blur(2px)',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ color: '#86eb85', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading company data...
                </Typography>
              </Box>
            </Box>
          )}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <CompanyProvider>
            <AuthProvider>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Protected Routes with Layout */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/employees" element={<Applications />} />
                  <Route path="/invoicing" element={<Invoicing />} />
                  <Route path="/payroll" element={<Payroll />} />
                  <Route path="/company-information" element={<CompanyInformation />} />
                </Route>

                {/* Root redirect */}
                <Route path="/" element={<Navigate to="/employees" replace />} />
              </Routes>

              <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
              />
            </AuthProvider>
          </CompanyProvider>
        </BrowserRouter>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;